"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { BRAND, NETWORK } from "@/lib/constant";
import { cn } from "@/lib/utils/utils";
interface TechStackChipsProps {
  name: string;
  techStack: string[];
}
const NODE_BG = "#212129";
const Chip = t0 => {
  const $ = _c(7);
  const {
    tech,
    important
  } = t0;
  const t1 = important ? "px-3.5 py-1.5 text-sm font-bold" : "px-3 py-1 text-xs font-semibold";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex items-center rounded-full border tracking-wide", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      color: BRAND,
      backgroundColor: NODE_BG,
      borderColor: `${BRAND}4d`
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full" style={{
      backgroundColor: BRAND
    }} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== tech) {
    t5 = <span className={t2} style={t3}>{t4}{tech}</span>;
    $[4] = t2;
    $[5] = tech;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  return t5;
};

/**
 * Small-screen fallback for the tech-sphere. The sphere is hover-driven and too
 * dense to read on phones, so here the stack renders as chips — the important
 * techs (listed first) show up front, the rest sit behind a "View more" toggle.
 */
const TechStackChips = t0 => {
  const $ = _c(30);
  const {
    name,
    techStack
  } = t0;
  const [expanded, setExpanded] = useState(false);
  const importantCount = Math.ceil(techStack.length * NETWORK.importantRatio);
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== expanded || $[1] !== importantCount || $[2] !== name || $[3] !== techStack) {
    const important = techStack.slice(0, importantCount);
    rest = techStack.slice(importantCount);
    t4 = "relative overflow-hidden rounded-[1.75rem] border border-black/6 bg-white p-6";
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div aria-hidden={true} className="pointer-events-none absolute inset-0" style={{
        background: `radial-gradient(80% 60% at 50% 0%, ${BRAND}12, transparent 70%)`
      }} />;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        color: NODE_BG
      };
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== name) {
      t8 = name.split(" ");
      $[13] = name;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    if ($[15] !== t8[0]) {
      t6 = <p className="relative mb-5 text-lg font-bold uppercase tracking-[0.3em]" style={t7}>{t8[0]}’s Stack</p>;
      $[15] = t8[0];
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t1 = "relative flex flex-wrap gap-2.5";
    t2 = important.map(_temp);
    t3 = expanded && rest.map(_temp2);
    $[0] = expanded;
    $[1] = importantCount;
    $[2] = name;
    $[3] = techStack;
    $[4] = rest;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    rest = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
  }
  let t7;
  if ($[17] !== t1 || $[18] !== t2 || $[19] !== t3) {
    t7 = <div className={t1}>{t2}{t3}</div>;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== expanded || $[22] !== rest.length) {
    t8 = rest.length > 0 && <button type="button" onClick={() => setExpanded(_temp3)} className="relative mt-5 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition-transform active:scale-95" style={{
      backgroundColor: NODE_BG
    }}>{expanded ? "View less" : `View more (+${rest.length})`}<ChevronDown className={cn("h-4 w-4 transition-transform duration-300", expanded && "rotate-180")} style={{
        color: BRAND
      }} /></button>;
    $[21] = expanded;
    $[22] = rest.length;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className={t4}>{t5}{t6}{t7}{t8}</div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
export default TechStackChips;
function _temp(tech, i) {
  return <Chip key={`${tech}-${i}`} tech={tech} important={true} />;
}
function _temp2(tech_0, i_0) {
  return <Chip key={`${tech_0}-rest-${i_0}`} tech={tech_0} important={false} />;
}
function _temp3(v) {
  return !v;
}